import { formatJson } from './components/flora-tool-call/flora-tool-call.helpers';
import { AutoModeOutcome } from './flora.type';
import type { Message, MessageSegment, ToolCall } from './flora.type';

/**
 * Websocket frame carrying a fragment of a tool call's arguments while the
 * model is still writing them (`is_tool_input_chunk`).
 */
interface ToolInputChunkFrame {
  tool_name: string;
  tool_call_id?: string | null;
  args?: string | null;
}

/**
 * Websocket frame announcing that the model finished writing a tool call's
 * arguments (`is_tool_input_end`). `tool_input` is the parsed final input when
 * the backend could resolve it.
 */
interface ToolInputEndFrame {
  tool_name: string;
  tool_call_id?: string | null;
  tool_input?: Record<string, unknown> | null;
}

/**
 * Websocket frame for a tool starting to execute (`is_tool_start`).
 */
interface ToolStartFrame {
  tool_name: string;
  tool_input?: Record<string, unknown> | null;
  parent_context?: string | null;
  /** Set to `approved` when auto mode ran the call without asking. */
  auto_mode?: AutoModeOutcome | null;
}

/**
 * Websocket frame for a tool call auto mode refused to run (`is_tool_blocked`).
 * A blocked call never receives `tool_start`/`tool_end`.
 */
interface ToolBlockedFrame {
  tool_name: string;
  tool_input?: Record<string, unknown> | null;
  tool_call_id?: string | null;
  parent_context?: string | null;
  auto_mode: AutoModeOutcome;
  /** Why the call could not run, sent for pre-validation (`invalid`) blocks. */
  tool_error?: string;
}

const AUTO_MODE_OUTCOMES: ReadonlySet<string> = new Set(Object.values(AutoModeOutcome));

/**
 * Coerce a websocket frame's raw `auto_mode` to a known outcome. Anything else
 * (absent, null, an unrecognized value) becomes `undefined` so an unexpected
 * backend value never renders a bogus tag.
 *
 * @param {unknown} value - The frame's raw `auto_mode`
 * @returns {AutoModeOutcome | undefined} The outcome when it is a known value
 * @example
 * toAutoModeOutcome('blocked') // AutoModeOutcome.Blocked
 * toAutoModeOutcome('maybe') // undefined
 */
const toAutoModeOutcome = (value: unknown): AutoModeOutcome | undefined =>
  isAutoModeOutcome(value) ? value : undefined;

/** Type guard for a known {@link AutoModeOutcome} value. */
function isAutoModeOutcome(value: unknown): value is AutoModeOutcome {
  return typeof value === 'string' && AUTO_MODE_OUTCOMES.has(value);
}

/**
 * Whether an auto-mode outcome means the call never ran (`blocked`,
 * `unavailable`, or pre-validation's `invalid`). The single source of truth for "blocked" across the
 * reducers, history parsing and the tool-call renderers.
 *
 * @param {AutoModeOutcome | null | undefined} outcome - The call's auto-mode outcome, if any
 * @returns {boolean} True when auto mode stopped the call
 * @example
 * isBlockingAutoModeOutcome(AutoModeOutcome.Unavailable) // true
 * isBlockingAutoModeOutcome(AutoModeOutcome.Approved) // false
 */
const isBlockingAutoModeOutcome = (outcome: AutoModeOutcome | null | undefined): boolean =>
  outcome === AutoModeOutcome.Blocked || outcome === AutoModeOutcome.Unavailable || outcome === AutoModeOutcome.Invalid;

/**
 * Build a {@link ToolBlockedFrame} from a raw `is_tool_blocked` websocket
 * frame. Returns `null` when the frame has no tool name or its `auto_mode` is
 * not a blocking outcome (`blocked` / `unavailable`), so a malformed frame is
 * dropped rather than rendered as a bogus row.
 *
 * @param {Record<string, unknown>} data - The parsed websocket frame
 * @returns {ToolBlockedFrame | null} The typed frame, or null when unusable
 * @example
 * parseToolBlockedFrame({ is_tool_blocked: true, tool_name: 'jira_update', auto_mode: 'blocked' })
 * // → { tool_name: 'jira_update', tool_input: null, tool_call_id: null, parent_context: null, auto_mode: 'blocked' }
 */
const parseToolBlockedFrame = (data: Record<string, unknown>): ToolBlockedFrame | null => {
  const autoMode = toAutoModeOutcome(data.auto_mode);
  if (typeof data.tool_name !== 'string' || !autoMode || !isBlockingAutoModeOutcome(autoMode)) {
    return null;
  }
  const toolInput = data.tool_input;
  return {
    tool_name: data.tool_name,
    tool_input:
      toolInput !== null && typeof toolInput === 'object' && !Array.isArray(toolInput)
        ? (toolInput as Record<string, unknown>)
        : null,
    tool_call_id: typeof data.tool_call_id === 'string' && data.tool_call_id ? data.tool_call_id : null,
    parent_context: typeof data.parent_context === 'string' && data.parent_context ? data.parent_context : null,
    auto_mode: autoMode,
    ...(typeof data.tool_error === 'string' && data.tool_error ? { tool_error: data.tool_error } : {}),
  };
};

/**
 * Whether a tool call is still in flight — either the model is writing its
 * arguments or the tool is executing. Used by the tool-call UIs to decide when
 * to show a spinner / "running" state.
 *
 * @param {ToolCall} toolCall - The tool call to inspect
 * @returns {boolean} True while the call is `streaming` or `pending`
 * @example
 * isToolCallInFlight({ status: 'streaming', ... }) // true
 * isToolCallInFlight({ status: 'drafted', ... }) // false
 */
const isToolCallInFlight = (toolCall: ToolCall): boolean =>
  toolCall.status === 'streaming' || toolCall.status === 'pending';

/**
 * Text to show in a tool call's input panel across every render surface.
 * While `streaming`, shows the raw argument fragments accumulated so far. Once
 * settled, shows the parsed input — but falls back to the raw fragments when
 * the parsed input is still empty (e.g. a `drafted` call whose JSON never
 * closed, or the backend resolved no input), so the panel is never a bare `{}`.
 *
 * @param {ToolCall} toolCall - The tool call to render input for
 * @returns {string} The input text to display
 */
const toolCallInputText = (toolCall: ToolCall): string => {
  if (toolCall.status === 'streaming') {
    return toolCall.partial_input ?? '';
  }
  if (Object.keys(toolCall.tool_input ?? {}).length === 0 && toolCall.partial_input) {
    return toolCall.partial_input;
  }
  return formatJson(toolCall.tool_input);
};

/**
 * Best-effort parse of accumulated partial-JSON tool arguments. Returns the
 * parsed object only when the fragments form a complete JSON object; otherwise
 * `null` so callers can fall back to whatever the backend resolved.
 *
 * @param {string | undefined} partialInput - Accumulated argument fragments
 * @returns {Record<string, unknown> | null} Parsed input or null when incomplete
 */
const parseCompletedInput = (partialInput: string | undefined): Record<string, unknown> | null => {
  if (!partialInput) return null;
  try {
    const parsed: unknown = JSON.parse(partialInput);
    return parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
};

/**
 * Whether `toolCall` is the streamed/drafted call that `frame` refers to.
 * Prefers the provider tool-call id when both sides carry one; otherwise falls
 * back to the tool name (the `tool_start` frame never carries an id).
 */
const matchesStreamedCall = (
  toolCall: ToolCall,
  frame: { tool_name: string; tool_call_id?: string | null },
  statuses: ReadonlyArray<ToolCall['status']>,
): boolean => {
  if (!statuses.includes(toolCall.status)) return false;
  if (toolCall.parent_context) return false;
  if (frame.tool_call_id && toolCall.tool_call_id) {
    return toolCall.tool_call_id === frame.tool_call_id;
  }
  return toolCall.tool_name === frame.tool_name;
};

/**
 * Apply `update` to the FIRST (oldest) tool call of the last message that
 * satisfies `predicate`, keeping `toolCalls` and `segments` in sync. Returns
 * `null` when there is no message or no matching tool call, so callers can
 * decide whether to fall back to creating a new call.
 *
 * FIFO matching is deliberate: when a frame carries no provider tool-call id
 * (a bare `tool_start`) and falls back to matching by tool name, it must pick
 * the same call that `tool_end`/`tool_error` will later resolve — those match
 * the first pending call of that name (`flora-hooks.ts`). Matching last here
 * instead would cross-wire two parallel same-name calls' inputs and outputs.
 */
const updateFirstMatchingToolCall = (
  prevMessages: Message[],
  predicate: (toolCall: ToolCall) => boolean,
  update: (toolCall: ToolCall) => ToolCall,
): Message[] | null => {
  if (prevMessages.length === 0) return null;
  const lastMessage = prevMessages[prevMessages.length - 1];
  const segments = lastMessage.segments || [];

  let targetId: string | null = null;
  for (const segment of segments) {
    if (segment.type === 'tool_call' && predicate(segment.toolCall)) {
      targetId = segment.toolCall.id;
      break;
    }
  }
  if (targetId === null) return null;

  const updatedSegments = segments.map((segment: MessageSegment) =>
    segment.type === 'tool_call' && segment.toolCall.id === targetId
      ? { ...segment, toolCall: update(segment.toolCall) }
      : segment,
  );
  const updatedToolCalls = (lastMessage.toolCalls || []).map((toolCall: ToolCall) =>
    toolCall.id === targetId ? update(toolCall) : toolCall,
  );

  const updatedMessages = [...prevMessages];
  updatedMessages[updatedMessages.length - 1] = {
    ...lastMessage,
    toolCalls: updatedToolCalls,
    segments: updatedSegments,
  };
  return updatedMessages;
};

/**
 * Append a new tool call (as both a `toolCalls` entry and an ordered segment)
 * to the last message.
 */
const appendToolCall = (prevMessages: Message[], toolCall: ToolCall): Message[] => {
  if (prevMessages.length === 0) return prevMessages;
  const updatedMessages = [...prevMessages];
  const lastMessage = updatedMessages[updatedMessages.length - 1];
  updatedMessages[updatedMessages.length - 1] = {
    ...lastMessage,
    toolCalls: [...(lastMessage.toolCalls || []), toolCall],
    segments: [...(lastMessage.segments || []), { type: 'tool_call', toolCall }],
  };
  return updatedMessages;
};

/**
 * Reducer for an `is_tool_input_chunk` frame. Appends the argument fragment to
 * the matching `streaming` tool call on the last message, or opens a new
 * `streaming` tool call when this is the first fragment.
 *
 * @param {Message[]} prevMessages - Current messages
 * @param {ToolInputChunkFrame} frame - The chunk frame
 * @param {() => string} createId - Factory for a new tool call's client id
 * @returns {Message[]} Updated messages (same reference when there is no message to update)
 * @example
 * setMessages((prev) => appendToolInputChunk(prev, data, () => createToolCallId(data.tool_name)));
 */
const appendToolInputChunk = (
  prevMessages: Message[],
  frame: ToolInputChunkFrame,
  createId: () => string,
): Message[] => {
  const args = frame.args ?? '';
  const updated = updateFirstMatchingToolCall(
    prevMessages,
    (toolCall) => matchesStreamedCall(toolCall, frame, ['streaming']),
    (toolCall) => ({
      ...toolCall,
      tool_call_id: toolCall.tool_call_id ?? frame.tool_call_id ?? null,
      partial_input: (toolCall.partial_input ?? '') + args,
    }),
  );
  if (updated) return updated;

  return appendToolCall(prevMessages, {
    id: createId(),
    tool_name: frame.tool_name,
    tool_call_id: frame.tool_call_id ?? null,
    tool_input: {},
    partial_input: args,
    status: 'streaming',
    parent_context: null,
  });
};

/**
 * Reducer for an `is_tool_input_end` frame. Marks the matching `streaming`
 * tool call as `drafted` and fills its `tool_input` from the backend-resolved
 * input, falling back to parsing the accumulated fragments.
 *
 * @param {Message[]} prevMessages - Current messages
 * @param {ToolInputEndFrame} frame - The end frame
 * @returns {Message[]} Updated messages (same reference when nothing matched)
 */
const finishToolInputStream = (prevMessages: Message[], frame: ToolInputEndFrame): Message[] => {
  const updated = updateFirstMatchingToolCall(
    prevMessages,
    (toolCall) => matchesStreamedCall(toolCall, frame, ['streaming']),
    (toolCall) => {
      const resolved = frame.tool_input && Object.keys(frame.tool_input).length > 0 ? frame.tool_input : null;
      return {
        ...toolCall,
        status: 'drafted',
        tool_input: resolved ?? parseCompletedInput(toolCall.partial_input) ?? toolCall.tool_input,
      };
    },
  );
  return updated ?? prevMessages;
};

/**
 * Reducer for an `is_tool_start` frame when the call may already exist as a
 * streamed/drafted tool call. Promotes that call to `pending` with the
 * authoritative input instead of appending a duplicate. Returns `null` when no
 * streamed call matches so the caller keeps its existing "create a new tool
 * call" behavior.
 *
 * @param {Message[]} prevMessages - Current messages
 * @param {ToolStartFrame} frame - The tool start frame
 * @returns {Message[] | null} Updated messages, or null when nothing was adopted
 */
const adoptStreamedToolCall = (prevMessages: Message[], frame: ToolStartFrame): Message[] | null => {
  if (frame.parent_context) return null;
  const promote = (toolCall: ToolCall): ToolCall => ({
    ...toolCall,
    status: 'pending',
    tool_input: frame.tool_input && Object.keys(frame.tool_input).length > 0 ? frame.tool_input : toolCall.tool_input,
    ...(frame.auto_mode ? { auto_mode: frame.auto_mode } : {}),
  });
  // A bare tool_start carries no provider id, so it matches by name. Prefer a
  // call still `streaming` over an older `drafted` one: a drafted call may be a
  // stale rejected draft from an earlier round, and adopting it would leave the
  // real retry (still streaming) unsettled. Fall back to a drafted call for the
  // normal approve-then-start flow, where no streaming call remains.
  // A call auto mode blocked is a settled `drafted` row that will never start;
  // a same-name retry must never adopt it, or the blocked row (and its audit
  // trail) would turn into the running retry.
  const isAdoptable = (toolCall: ToolCall, statuses: ReadonlyArray<ToolCall['status']>): boolean =>
    matchesStreamedCall(toolCall, frame, statuses) && !isBlockingAutoModeOutcome(toolCall.auto_mode);
  return (
    updateFirstMatchingToolCall(prevMessages, (toolCall) => isAdoptable(toolCall, ['streaming']), promote) ??
    updateFirstMatchingToolCall(prevMessages, (toolCall) => isAdoptable(toolCall, ['drafted']), promote)
  );
};

/**
 * Reducer for an `is_tool_blocked` frame. When the blocked call's arguments
 * were already streamed (e.g. an eager-streamed `flora_page_publish`), that
 * streamed call is settled to `drafted` and tagged with the auto-mode outcome
 * instead of leaving a dangling "Drafted" row beside a new one. Adoption
 * requires a provider tool-call id on both sides — a name-only match could
 * relabel an unrelated rejected draft. Otherwise a new `drafted` call is
 * appended, carrying `parent_context` so subagent calls nest correctly.
 *
 * @param {Message[]} prevMessages - Current messages
 * @param {ToolBlockedFrame} frame - The tool blocked frame
 * @param {() => string} createId - Factory for a new tool call's client id
 * @returns {Message[]} Updated messages (same reference when there is no message to update)
 * @example
 * setMessages((prev) => applyAutoModeBlock(prev, frame, () => createToolCallId(frame.tool_name)));
 */
const applyAutoModeBlock = (prevMessages: Message[], frame: ToolBlockedFrame, createId: () => string): Message[] => {
  const resolvedInput = frame.tool_input && Object.keys(frame.tool_input).length > 0 ? frame.tool_input : null;

  if (!frame.parent_context && frame.tool_call_id) {
    const adopted = updateFirstMatchingToolCall(
      prevMessages,
      (toolCall) =>
        matchesStreamedCall(toolCall, frame, ['streaming', 'drafted']) &&
        Boolean(toolCall.tool_call_id) &&
        !toolCall.auto_mode,
      (toolCall) => ({
        ...toolCall,
        status: 'drafted',
        auto_mode: frame.auto_mode,
        tool_input: resolvedInput ?? parseCompletedInput(toolCall.partial_input) ?? toolCall.tool_input,
        // Always the frame's error, so an earlier one never survives the block.
        tool_error: frame.tool_error,
      }),
    );
    if (adopted) return adopted;
  }

  return appendToolCall(prevMessages, {
    id: createId(),
    tool_name: frame.tool_name,
    tool_call_id: frame.tool_call_id ?? null,
    tool_input: resolvedInput ?? {},
    status: 'drafted',
    parent_context: frame.parent_context ?? null,
    auto_mode: frame.auto_mode,
    ...(frame.tool_error ? { tool_error: frame.tool_error } : {}),
  });
};

/**
 * Settle every still-`streaming` tool call on the last message to `drafted`.
 * Used when a run ends (done, error, cancel) so a spinner never survives the
 * turn for arguments the model stopped writing.
 *
 * @param {Message[]} prevMessages - Current messages
 * @returns {Message[]} Updated messages (same reference when nothing was streaming)
 */
const settleStreamingToolCalls = (prevMessages: Message[]): Message[] => {
  if (prevMessages.length === 0) return prevMessages;
  const lastMessage = prevMessages[prevMessages.length - 1];
  const hasStreaming = (lastMessage.segments || []).some(
    (segment) => segment.type === 'tool_call' && segment.toolCall.status === 'streaming',
  );
  if (!hasStreaming) return prevMessages;

  const settle = (toolCall: ToolCall): ToolCall =>
    toolCall.status === 'streaming'
      ? {
          ...toolCall,
          status: 'drafted',
          tool_input: parseCompletedInput(toolCall.partial_input) ?? toolCall.tool_input,
        }
      : toolCall;

  const updatedMessages = [...prevMessages];
  updatedMessages[updatedMessages.length - 1] = {
    ...lastMessage,
    toolCalls: (lastMessage.toolCalls || []).map(settle),
    segments: (lastMessage.segments || []).map((segment) =>
      segment.type === 'tool_call' ? { ...segment, toolCall: settle(segment.toolCall) } : segment,
    ),
  };
  return updatedMessages;
};

export {
  adoptStreamedToolCall,
  applyAutoModeBlock,
  appendToolInputChunk,
  finishToolInputStream,
  isBlockingAutoModeOutcome,
  isToolCallInFlight,
  parseToolBlockedFrame,
  settleStreamingToolCalls,
  toAutoModeOutcome,
  toolCallInputText,
};
